import { useEffect, useRef, useState } from "react";
import type {
  Character,
  FilmProject,
  ParsedReferenceBoard,
  SeedanceVoicePreset,
  SourceFocusTarget,
  UploadedAssetManifest,
  UploadedAssetRecord,
  WorkspaceSection
} from "../types";
import { sceneStatus } from "../utils/storyboard";
import { Icon } from "./Icon";

type AssetBucket = keyof UploadedAssetManifest;
type SourceSection = Exclude<WorkspaceSection, "projects" | "storyboards">;

interface SourceWorkspaceProps {
  project: FilmProject;
  activeSection: SourceSection;
  onSectionChange: (section: SourceSection) => void;
  onProjectChange: (changes: Partial<FilmProject>) => void;
  onUploadAssets: (bucket: AssetBucket, files: File[], context?: Partial<UploadedAssetRecord>) => void;
  onRetryAssetUpload: (bucket: AssetBucket, assetId: string) => void;
  onParseReference: (asset: UploadedAssetRecord) => void;
  parsingReferenceId?: string;
  focus?: SourceFocusTarget | null;
  onReturnFromFocus?: (focus: SourceFocusTarget) => void;
}

const emptyAssets: UploadedAssetManifest = {
  characterCards: [],
  sceneReferences: [],
  voiceAudio: [],
  music: [],
  sfx: [],
  videoReferences: [],
  styleRefs: [],
  branding: [],
  documents: []
};

const seedanceVoicePresets: Array<{ id: SeedanceVoicePreset; label: string; detail: string }> = [
  { id: "cinematic_natural", label: "Cinematic natural", detail: "Grounded film dialogue" },
  { id: "rugged_noir", label: "Rugged noir", detail: "Low, weathered, streetwise" },
  { id: "calm_warm", label: "Calm warm", detail: "Gentle and emotionally present" },
  { id: "corporate_polished", label: "Corporate polished", detail: "Clean premium VO" },
  { id: "authoritative", label: "Authoritative", detail: "Steady, decisive, mature" },
  { id: "tense_whisper", label: "Tense whisper", detail: "Urgent thriller restraint" },
  { id: "exhausted_broken", label: "Exhausted broken", detail: "Worn down, emotional" },
  { id: "comedic_dry", label: "Comedic dry", detail: "Understated and skeptical" },
  { id: "youthful_bright", label: "Youthful bright", detail: "Quick, casual energy" },
  { id: "villain_silky", label: "Villain silky", detail: "Smooth controlled menace" }
];

const sectionTabs: Array<{ id: SourceSection; label: string; description: string }> = [
  { id: "characters", label: "Characters", description: "Cards, wardrobe, locks" },
  { id: "voices", label: "Voices", description: "Actor audio or EvoLink Seedance" },
  { id: "references", label: "References", description: "Scene looks and worlds" }
];

function createId(prefix: string): string {
  return `${prefix}-${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;
}

function characterInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "CH";
}

function uploadedAssets(project: FilmProject): UploadedAssetManifest {
  return project.uploadedAssets || emptyAssets;
}

function cardStatusLabel(character: Character): string {
  if (character.cardDisabled) return "Disabled";
  if (character.cardSource === "seed_generated" || character.cardStatus === "generated") return "EvoLink Seedance";
  if (character.cardStatus === "uploaded") return "User upload";
  return "Missing";
}

function voiceStatusLabel(character: Character): string {
  if (character.voiceDisabled) return "Disabled";
  if (character.voiceStatus === "not_needed") return "No voice";
  if (character.voiceSource === "seed_generated") return "EvoLink Seedance voice";
  if (character.voiceSource === "user_upload") return "User audio";
  return character.voiceStatus === "assigned" ? "Assigned" : "Missing";
}

function referenceStatusLabel(asset: UploadedAssetRecord): string {
  if (asset.disabled) return "Disabled";
  if (asset.sourceMode === "seed_generated" || asset.provider === "evolink" || asset.provider === "seed") return "EvoLink Seedance";
  return "User upload";
}

function referenceParseLabel(asset: UploadedAssetRecord): string {
  if (asset.parseStatus === "parsing") return "Parsing";
  if (asset.parseStatus === "parsed") return asset.parsedReference?.provider === "openai" ? "Parsed by OpenAI" : "Parsed";
  if (asset.parseStatus === "not_configured") return "Fallback parsed";
  if (asset.parseStatus === "unsupported") return "Unsupported";
  if (asset.parseStatus === "failed") return "Parse failed";
  return "Unparsed";
}

function assetCloudStatusLabel(asset?: UploadedAssetRecord): string {
  if (!asset) return "";
  if (asset.cloudStatus === "pending") return "Upload pending";
  if (asset.cloudStatus === "failed") return "Upload failed";
  if (asset.cloudStatus === "local_only") return "Local only";
  if (asset.cloudStatus === "uploaded") return "Cloud saved";
  return asset.cloudStatus ? String(asset.cloudStatus) : "";
}

function assetCloudStatusClass(asset?: UploadedAssetRecord): string {
  if (!asset?.cloudStatus) return "";
  return `cloud-${String(asset.cloudStatus).replace(/[^a-z0-9_-]+/gi, "-").toLowerCase()}`;
}

function assetRetryable(asset?: UploadedAssetRecord): asset is UploadedAssetRecord & { assetId: string } {
  return Boolean(asset?.assetId && asset.cloudStatus === "failed");
}

function latestAssetForCharacter(records: UploadedAssetRecord[], character: Character): UploadedAssetRecord | undefined {
  const characterName = character.name.toLowerCase();
  return records
    .filter((asset) => (
      asset.characterName?.toLowerCase() === characterName ||
      asset.label.toLowerCase().includes(characterName) ||
      asset.fileName.toLowerCase().includes(characterName)
    ))
    .slice(-1)[0];
}

function compactList(items: string[] | undefined, max = 3): string {
  return (items || []).filter(Boolean).slice(0, max).join("; ");
}

function referenceReviewRows(parsed: ParsedReferenceBoard): Array<{ label: string; value: string }> {
  return [
    { label: "World", value: parsed.storyWorld },
    { label: "Time", value: parsed.timePeriod },
    { label: "Location", value: parsed.location },
    { label: "Design", value: compactList(parsed.designNotes, 6) },
    { label: "Camera", value: compactList(parsed.cameraLens, 6) },
    { label: "Shots", value: compactList(parsed.shotReferences, 6) },
    { label: "Characters", value: compactList(parsed.characterReferences, 6) },
    { label: "Wardrobe", value: compactList(parsed.wardrobe, 6) },
    { label: "Props", value: compactList(parsed.propsVehicles, 6) },
    { label: "Lighting", value: compactList(parsed.lightingAtmosphere, 6) },
    { label: "Sound", value: compactList(parsed.soundDesign, 6) },
    { label: "Rules", value: compactList(parsed.brandingWorldRules, 6) },
    { label: "Palette", value: compactList(parsed.colorPalette, 8) },
    { label: "Text", value: compactList(parsed.textTypography, 6) },
    { label: "Boards", value: compactList(parsed.storyboardSuggestions, 6) },
    { label: "Prompt", value: compactList(parsed.promptNotes, 6) }
  ].filter((row) => row.value);
}

function referencePromptBlock(asset: UploadedAssetRecord): string {
  const parsed = asset.parsedReference;
  if (!parsed) return "";
  const rows = referenceReviewRows(parsed)
    .filter((row) => ["World", "Location", "Design", "Camera", "Lighting", "Palette", "Prompt"].includes(row.label))
    .slice(0, 7);
  if (!rows.length) return "";
  return rows.map((row) => `${row.label}: ${row.value}`).join("\n");
}

function promptWithReferenceNotes(prompt: string | undefined, asset: UploadedAssetRecord): string | undefined {
  const notes = referencePromptBlock(asset);
  if (!notes) return prompt;
  const marker = `Reference board notes (${asset.assetId || asset.label})`;
  const existing = (prompt || "").trim();
  if (existing.includes(marker)) return prompt;
  return [existing, `${marker}:\n${notes}`].filter(Boolean).join("\n\n");
}

function selectorValue(value: string): string {
  return value.replace(/["\\]/g, "\\$&");
}

function focusMatchesCharacter(focus: SourceFocusTarget | null | undefined, character: Character): boolean {
  if (!focus || (focus.section !== "characters" && focus.section !== "voices")) return false;
  return Boolean(
    focus.characterId === character.id ||
    (focus.characterName && focus.characterName.toLowerCase() === character.name.toLowerCase())
  );
}

export function SourceWorkspace({
  project,
  activeSection,
  onSectionChange,
  onProjectChange,
  onUploadAssets,
  onRetryAssetUpload,
  onParseReference,
  parsingReferenceId,
  focus,
  onReturnFromFocus
}: SourceWorkspaceProps) {
  const [reviewReferenceId, setReviewReferenceId] = useState<string | null>(null);
  const assets = uploadedAssets(project);
  const reviewReferenceAsset = reviewReferenceId
    ? assets.sceneReferences.find((asset) => asset.assetId === reviewReferenceId) || null
    : null;
  const readyCards = project.characters.filter((character) => character.cardStatus !== "missing" && !character.cardDisabled).length;
  const readyVoices = project.characters.filter((character) => character.voiceStatus === "assigned" && !character.voiceDisabled).length;
  const activeRefs = assets.sceneReferences.filter((asset) => !asset.disabled).length;
  const returnLabel = focus?.returnTo === "generate" ? "Return to Generate" : "Return to Review";

  useEffect(() => {
    if (!focus || focus.section !== activeSection) return;
    const selector = focus.characterId
      ? `[data-source-character-id="${selectorValue(focus.characterId)}"]`
      : focus.sceneId
        ? `[data-source-scene-id="${selectorValue(focus.sceneId)}"]`
        : "";
    if (!selector) return;
    window.requestAnimationFrame(() => {
      document.querySelector(selector)?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [activeSection, focus]);

  function replaceCharacters(characters: Character[], scenes = project.scenes) {
    onProjectChange({ characters, scenes });
  }

  function updateCharacter(characterId: string, changes: Partial<Character>) {
    replaceCharacters(project.characters.map((character) => (character.id === characterId ? { ...character, ...changes } : character)));
  }

  function renameCharacter(character: Character, name: string) {
    const nextName = name || character.name;
    replaceCharacters(
      project.characters.map((item) => (item.id === character.id ? { ...item, name: nextName } : item)),
      project.scenes.map((scene) => ({
        ...scene,
        characters: scene.characters.map((sceneCharacter) => (sceneCharacter === character.name ? nextName : sceneCharacter))
      }))
    );
  }

  function markScenesReadyForCharacter(characterName: string, readiness: "needs_character" | "needs_voice") {
    return project.scenes.map((scene) => (
      scene.readiness === readiness && scene.characters.includes(characterName) ? { ...scene, readiness: "ready" as const } : scene
    ));
  }

  function uploadCharacterCard(character: Character, files: File[]) {
    onUploadAssets("characterCards", files, {
      kind: "character_card",
      label: `${character.name} character card`,
      characterName: character.name
    });
    replaceCharacters(
      project.characters.map((item) => (
        item.id === character.id
          ? {
              ...item,
              cardStatus: "uploaded",
              cardSource: "user_upload",
              cardAssetLabel: files[0]?.name || "Uploaded character card",
              cardLocked: true,
              cardDisabled: false
            }
          : item
      )),
      markScenesReadyForCharacter(character.name, "needs_character")
    );
  }

  function setSeedGeneratedCharacter(character: Character) {
    replaceCharacters(
      project.characters.map((item) => (
        item.id === character.id
          ? {
              ...item,
              cardStatus: "generated",
              cardSource: "seed_generated",
              cardAssetLabel: "EvoLink Seedance generated character card",
              cardDisabled: false,
              seedCharacterPrompt: item.seedCharacterPrompt || `${item.name}, ${item.role}, consistent wardrobe and face, cinematic character reference`
            }
          : item
      )),
      markScenesReadyForCharacter(character.name, "needs_character")
    );
  }

  function uploadVoiceAudio(character: Character, files: File[]) {
    onUploadAssets("voiceAudio", files, {
      kind: "voice_audio",
      label: `${character.name} actor audio`,
      characterName: character.name
    });
    replaceCharacters(
      project.characters.map((item) => (
        item.id === character.id
          ? {
              ...item,
              voiceStatus: "assigned",
              voiceSource: "user_upload",
              voiceAssetLabel: files[0]?.name || "Uploaded actor audio",
              voiceDisabled: false
            }
          : item
      )),
      markScenesReadyForCharacter(character.name, "needs_voice")
    );
  }

  function setSeedanceVoice(character: Character) {
    replaceCharacters(
      project.characters.map((item) => (
        item.id === character.id
          ? {
              ...item,
              voiceStatus: "assigned",
              voiceSource: "seed_generated",
              voicePreset: item.voicePreset || "cinematic_natural",
              voiceAssetLabel: "EvoLink Seedance generated voice",
              voiceDisabled: false
            }
          : item
      )),
      markScenesReadyForCharacter(character.name, "needs_voice")
    );
  }

  function setNoVoice(character: Character) {
    replaceCharacters(
      project.characters.map((item) => (
        item.id === character.id
          ? {
              ...item,
              voiceStatus: "not_needed",
              voiceSource: "not_needed",
              voiceDisabled: false,
              voiceAssetLabel: "No voice needed"
            }
          : item
      )),
      markScenesReadyForCharacter(character.name, "needs_voice")
    );
  }

  function addCharacter() {
    const index = project.characters.length + 1;
    const name = `Character ${index}`;
    onProjectChange({
      characters: [
        ...project.characters,
        {
          id: createId("char"),
          name,
          role: "New role",
          cardStatus: "missing",
          voiceStatus: "missing",
          cardSource: "missing",
          voiceSource: "missing"
        }
      ]
    });
  }

  function removeCharacter(characterId: string) {
    const target = project.characters.find((character) => character.id === characterId);
    if (!target) return;
    onProjectChange({
      characters: project.characters.filter((character) => character.id !== characterId),
      scenes: project.scenes.map((scene) => ({
        ...scene,
        characters: scene.characters.filter((characterName) => characterName !== target.name)
      }))
    });
  }

  function uploadSceneReference(sceneId: string, files: File[]) {
    const scene = project.scenes.find((item) => item.id === sceneId);
    if (!scene) return;
    onUploadAssets("sceneReferences", files, {
      kind: "scene_reference",
      label: `${scene.slugline} scene reference`,
      sceneId
    });
    onProjectChange({
      scenes: project.scenes.map((item) => (
        item.id === sceneId && item.readiness === "missing_reference" ? { ...item, readiness: "ready" } : item
      ))
    });
  }

  function addSeedReference(sceneId: string) {
    const scene = project.scenes.find((item) => item.id === sceneId);
    if (!scene) return;
    const record: UploadedAssetRecord = {
      assetId: createId("seed-ref"),
      kind: "scene_reference",
      label: `${scene.location || scene.slugline} EvoLink reference`,
      fileName: "EvoLink Seedance generated scene reference",
      sourceOfTruth: false,
      locked: false,
      disabled: false,
      sourceMode: "seed_generated",
      provider: "evolink",
      sceneId,
      uploadedAt: new Date().toISOString()
    };
    onProjectChange({
      uploadedAssets: {
        ...assets,
        sceneReferences: [...assets.sceneReferences, record]
      },
      scenes: project.scenes.map((item) => (
        item.id === sceneId && item.readiness === "missing_reference" ? { ...item, readiness: "ready" } : item
      ))
    });
  }

  function toggleReferenceDisabled(assetId: string) {
    onProjectChange({
      uploadedAssets: {
        ...assets,
        sceneReferences: assets.sceneReferences.map((asset) => (
          asset.assetId === assetId ? { ...asset, disabled: !asset.disabled } : asset
        ))
      }
    });
  }

  function applyParsedReferenceToScene(asset: UploadedAssetRecord) {
    if (!asset.assetId || !asset.sceneId || !asset.parsedReference) return;
    onProjectChange({
      scenes: project.scenes.map((scene) => {
        if (scene.id !== asset.sceneId) return scene;
        return {
          ...scene,
          location: asset.parsedReference?.location || scene.location,
          readiness: scene.readiness === "missing_reference" ? "ready" : scene.readiness,
          frames: scene.frames.map((frame) => {
            const prompt = promptWithReferenceNotes(frame.prompt, asset);
            if (!frame.sourceAssignments) {
              return { ...frame, prompt };
            }
            const assignments = frame.sourceAssignments;
            const referenceAssetIds = assignments.referenceAssetIds.includes(asset.assetId!)
              ? assignments.referenceAssetIds
              : [...assignments.referenceAssetIds, asset.assetId!];
            return {
              ...frame,
              prompt,
              sourceAssignments: {
                ...assignments,
                referenceAssetIds
              }
            };
          })
        };
      })
    });
    setReviewReferenceId(null);
  }

  return (
    <section className="source-workspace" aria-label="Characters voices and references">
      <header className="source-header">
        <div>
          <span>Production Sources</span>
          <h1>Characters, voices, and references</h1>
          <p>Assign every source before EvoLink review. Use uploaded assets as truth, or mark empty slots for EvoLink Seedance-generated material.</p>
        </div>
        <div className="source-summary-grid">
          <div><span>Cards</span><strong>{readyCards}/{project.characters.length}</strong></div>
          <div><span>Voices</span><strong>{readyVoices}/{project.characters.length}</strong></div>
          <div><span>References</span><strong>{activeRefs}</strong></div>
        </div>
      </header>

      {focus && focus.section === activeSection ? (
        <div className="source-focus-banner" role="status">
          <Icon name={activeSection === "voices" ? "mic" : activeSection === "references" ? "image" : "boards"} />
          <span>Resolving</span>
          <strong>{focus.label || focus.characterName || "missing input"}</strong>
          <small>Choose user upload or EvoLink Seedance generation.</small>
          {focus && onReturnFromFocus ? (
            <button className="secondary-action compact-action" onClick={() => onReturnFromFocus(focus)} type="button">
              {returnLabel}
            </button>
          ) : null}
        </div>
      ) : null}

      <nav className="source-tabs" aria-label="Source sections">
        {sectionTabs.map((tab) => (
          <button className={activeSection === tab.id ? "active" : ""} key={tab.id} onClick={() => onSectionChange(tab.id)} type="button">
            <strong>{tab.label}</strong>
            <small>{tab.description}</small>
          </button>
        ))}
      </nav>

      {activeSection === "characters" ? (
        <CharactersSection
          characters={project.characters}
          assets={assets}
          focus={focus}
          onAddCharacter={addCharacter}
          onRemoveCharacter={removeCharacter}
          onRenameCharacter={renameCharacter}
          onUpdateCharacter={updateCharacter}
          onUploadCharacterCard={uploadCharacterCard}
          onUploadAssets={onUploadAssets}
          onRetryAssetUpload={onRetryAssetUpload}
          onSeedCharacter={setSeedGeneratedCharacter}
        />
      ) : null}

      {activeSection === "voices" ? (
        <VoicesSection
          characters={project.characters}
          assets={assets}
          focus={focus}
          onAddCharacter={addCharacter}
          onUpdateCharacter={updateCharacter}
          onUploadAssets={onUploadAssets}
          onUploadVoiceAudio={uploadVoiceAudio}
          onSeedanceVoice={setSeedanceVoice}
          onNoVoice={setNoVoice}
          onRetryAssetUpload={onRetryAssetUpload}
          onRemoveVoiceCharacter={removeCharacter}
        />
      ) : null}

      {activeSection === "references" ? (
        <ReferencesSection
          assets={assets}
          focus={focus}
          project={project}
          onUploadAssets={onUploadAssets}
          onUploadSceneReference={uploadSceneReference}
          onSeedReference={addSeedReference}
          onToggleReferenceDisabled={toggleReferenceDisabled}
          onRetryAssetUpload={onRetryAssetUpload}
          onParseReference={onParseReference}
          onReviewReference={setReviewReferenceId}
          parsingReferenceId={parsingReferenceId}
        />
      ) : null}

      {reviewReferenceAsset?.parsedReference ? (
        <ReferenceParseReviewModal
          asset={reviewReferenceAsset}
          onApply={() => applyParsedReferenceToScene(reviewReferenceAsset)}
          onClose={() => setReviewReferenceId(null)}
        />
      ) : null}
    </section>
  );
}

function CharactersSection({
  characters,
  assets,
  focus,
  onAddCharacter,
  onRemoveCharacter,
  onRenameCharacter,
  onUpdateCharacter,
  onUploadCharacterCard,
  onUploadAssets,
  onRetryAssetUpload,
  onSeedCharacter
}: {
  characters: Character[];
  assets: UploadedAssetManifest;
  focus?: SourceFocusTarget | null;
  onAddCharacter: () => void;
  onRemoveCharacter: (characterId: string) => void;
  onRenameCharacter: (character: Character, name: string) => void;
  onUpdateCharacter: (characterId: string, changes: Partial<Character>) => void;
  onUploadCharacterCard: (character: Character, files: File[]) => void;
  onUploadAssets: (bucket: AssetBucket, files: File[], context?: Partial<UploadedAssetRecord>) => void;
  onRetryAssetUpload: (bucket: AssetBucket, assetId: string) => void;
  onSeedCharacter: (character: Character) => void;
}) {
  return (
    <div className="source-panel">
      <div className="source-panel-heading">
        <div>
          <span>Character Cards</span>
          <h2>Identity, wardrobe, face, and continuity</h2>
        </div>
        <button className="secondary-action" onClick={onAddCharacter} type="button">
          <Icon name="plus" />
          Add Character
        </button>
      </div>
      <div className="source-card-grid">
        {characters.length ? characters.map((character) => {
          const cardAsset = latestAssetForCharacter(assets.characterCards, character);
          const cloudLabel = assetCloudStatusLabel(cardAsset);
          return (
            <article
              className={`source-card ${character.cardDisabled ? "disabled" : ""} ${focusMatchesCharacter(focus, character) ? "focused" : ""}`}
              data-source-character-id={character.id}
              key={character.id}
            >
            <div className="source-avatar">{characterInitials(character.name)}</div>
            <div className="source-card-main">
              <div className="source-field-grid">
                <label>
                  <span>Name</span>
                  <input value={character.name} onChange={(event) => onRenameCharacter(character, event.target.value)} />
                </label>
                <label>
                  <span>Role</span>
                  <input value={character.role} onChange={(event) => onUpdateCharacter(character.id, { role: event.target.value })} />
                </label>
              </div>

              <div className="asset-status-row">
                <span className={`source-pill ${character.cardStatus}`}>{cardStatusLabel(character)}</span>
                {character.cardLocked ? <span className="source-pill locked">Locked</span> : null}
                {cloudLabel ? <span className={`source-pill ${assetCloudStatusClass(cardAsset)}`}>{cloudLabel}</span> : null}
                {character.cardAssetLabel ? <small>{character.cardAssetLabel}</small> : <small>No character card assigned</small>}
              </div>

              {character.cardSource === "seed_generated" ? (
                <label className="source-prompt-field">
                  <span>EvoLink character prompt</span>
                  <textarea
                    value={character.seedCharacterPrompt || ""}
                    onChange={(event) => onUpdateCharacter(character.id, { seedCharacterPrompt: event.target.value })}
                  />
                </label>
              ) : null}

              <div className="source-action-row">
                <UploadAssetButton accept="image/*,.pdf" label="Upload card" onFiles={(files) => onUploadCharacterCard(character, files)} />
                <button className="secondary-action" onClick={() => onSeedCharacter(character)} type="button">EvoLink Seedance card</button>
                <button
                  className="secondary-action"
                  onClick={() => onUpdateCharacter(character.id, { cardLocked: !character.cardLocked })}
                  type="button"
                >
                  {character.cardLocked ? "Unlock" : "Lock"}
                </button>
                <button
                  className="secondary-action"
                  onClick={() => onUpdateCharacter(character.id, { cardDisabled: !character.cardDisabled })}
                  type="button"
                >
                  {character.cardDisabled ? "Enable" : "Disable"}
                </button>
                <button className="secondary-action danger" onClick={() => onRemoveCharacter(character.id)} type="button">Remove</button>
                {assetRetryable(cardAsset) ? (
                  <button className="secondary-action" onClick={() => onRetryAssetUpload("characterCards", cardAsset.assetId)} type="button">
                    Retry upload
                  </button>
                ) : null}
              </div>
            </div>
          </article>
          );
        }) : (
          <section className="source-empty-state">
            <Icon name="boards" />
            <strong>No characters yet</strong>
            <p>Add a character manually, analyze a script, or upload a card now so it is ready when the role is assigned.</p>
            <div className="source-empty-actions">
              <button className="primary-action" onClick={onAddCharacter} type="button">
                <Icon name="plus" />
                Add Character
              </button>
              <UploadAssetButton
                accept="image/*,.pdf"
                label="Upload card"
                onFiles={(files) => onUploadAssets("characterCards", files, { kind: "character_card", label: "Unassigned character card" })}
              />
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function VoicesSection({
  characters,
  assets,
  focus,
  onAddCharacter,
  onUpdateCharacter,
  onUploadAssets,
  onUploadVoiceAudio,
  onSeedanceVoice,
  onNoVoice,
  onRetryAssetUpload,
  onRemoveVoiceCharacter
}: {
  characters: Character[];
  assets: UploadedAssetManifest;
  focus?: SourceFocusTarget | null;
  onAddCharacter: () => void;
  onUpdateCharacter: (characterId: string, changes: Partial<Character>) => void;
  onUploadAssets: (bucket: AssetBucket, files: File[], context?: Partial<UploadedAssetRecord>) => void;
  onUploadVoiceAudio: (character: Character, files: File[]) => void;
  onSeedanceVoice: (character: Character) => void;
  onNoVoice: (character: Character) => void;
  onRetryAssetUpload: (bucket: AssetBucket, assetId: string) => void;
  onRemoveVoiceCharacter: (characterId: string) => void;
}) {
  return (
    <div className="source-panel">
      <div className="source-panel-heading">
        <div>
          <span>Voices</span>
          <h2>Actor audio or EvoLink Seedance generated speech</h2>
          <p>Only real speakers should stay here. Remove false positives like CUT TO, CLOSE ON, INSERT, or title cards.</p>
        </div>
      </div>
      <div className="voice-list">
        {characters.length ? characters.map((character) => {
          const voiceAsset = latestAssetForCharacter(assets.voiceAudio, character);
          const cloudLabel = assetCloudStatusLabel(voiceAsset);
          return (
            <article
              className={`voice-row ${character.voiceStatus === "not_needed" || character.voiceDisabled ? "disabled" : ""} ${focusMatchesCharacter(focus, character) ? "focused" : ""}`}
              data-source-character-id={character.id}
              key={character.id}
            >
            <div className="source-avatar compact">{characterInitials(character.name)}</div>
            <div>
              <strong>{character.name}</strong>
              <small>{character.voiceAssetLabel ? `${character.role} - ${character.voiceAssetLabel}` : character.role}</small>
            </div>
            <div className="voice-status-stack">
              <span className={`source-pill ${character.voiceStatus}`}>{voiceStatusLabel(character)}</span>
              {cloudLabel ? <span className={`source-pill ${assetCloudStatusClass(voiceAsset)}`}>{cloudLabel}</span> : null}
            </div>
            <label>
              <span>EvoLink Seedance voice preset</span>
              <select
                value={character.voicePreset || "cinematic_natural"}
                onChange={(event) => onUpdateCharacter(character.id, { voicePreset: event.target.value as SeedanceVoicePreset })}
                disabled={character.voiceStatus === "not_needed"}
              >
                {seedanceVoicePresets.map((preset) => (
                  <option key={preset.id} value={preset.id}>{preset.label} - {preset.detail}</option>
                ))}
              </select>
            </label>
            <div className="source-action-row compact">
              <UploadAssetButton accept="audio/*,.m4a,.mp3,.wav,.aiff" label="Upload actor audio" onFiles={(files) => onUploadVoiceAudio(character, files)} />
              <button className="secondary-action" onClick={() => onSeedanceVoice(character)} type="button">EvoLink Seedance</button>
              <button
                className="secondary-action"
                onClick={() => onNoVoice(character)}
                type="button"
              >
                No voice
              </button>
              <button
                className="secondary-action"
                onClick={() => onUpdateCharacter(character.id, { voiceDisabled: !character.voiceDisabled })}
                type="button"
              >
                {character.voiceDisabled ? "Enable" : "Disable"}
              </button>
              <button
                className="secondary-action danger"
                onClick={() => onRemoveVoiceCharacter(character.id)}
                type="button"
              >
                Remove row
              </button>
              {assetRetryable(voiceAsset) ? (
                <button className="secondary-action" onClick={() => onRetryAssetUpload("voiceAudio", voiceAsset.assetId)} type="button">
                  Retry upload
                </button>
              ) : null}
            </div>
          </article>
          );
        }) : (
          <section className="source-empty-state">
            <Icon name="mic" />
            <strong>No voice slots yet</strong>
            <p>Add a speaking character, analyze a script, or upload actor audio now and assign it after the speaker exists.</p>
            <div className="source-empty-actions">
              <button className="primary-action" onClick={onAddCharacter} type="button">
                <Icon name="plus" />
                Add Character
              </button>
              <UploadAssetButton
                accept="audio/*,.m4a,.mp3,.wav,.aiff"
                label="Upload actor audio"
                onFiles={(files) => onUploadAssets("voiceAudio", files, { kind: "voice_audio", label: "Unassigned actor audio" })}
              />
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function ReferencesSection({
  project,
  assets,
  focus,
  onUploadAssets,
  onUploadSceneReference,
  onSeedReference,
  onToggleReferenceDisabled,
  onRetryAssetUpload,
  onParseReference,
  onReviewReference,
  parsingReferenceId
}: {
  project: FilmProject;
  assets: UploadedAssetManifest;
  focus?: SourceFocusTarget | null;
  onUploadAssets: (bucket: AssetBucket, files: File[], context?: Partial<UploadedAssetRecord>) => void;
  onUploadSceneReference: (sceneId: string, files: File[]) => void;
  onSeedReference: (sceneId: string) => void;
  onToggleReferenceDisabled: (assetId: string) => void;
  onRetryAssetUpload: (bucket: AssetBucket, assetId: string) => void;
  onParseReference: (asset: UploadedAssetRecord) => void;
  onReviewReference: (assetId: string) => void;
  parsingReferenceId?: string;
}) {
  function referencesForScene(sceneId: string): UploadedAssetRecord[] {
    return assets.sceneReferences.filter((asset) => asset.sceneId === sceneId);
  }

  return (
    <div className="source-panel">
      <div className="source-panel-heading">
        <div>
          <span>Scene References</span>
          <h2>Backgrounds, locations, production design, and tone</h2>
        </div>
      </div>
      <div className="reference-scene-list">
        {project.scenes.length ? project.scenes.map((scene) => {
          const sceneReferences = referencesForScene(scene.id);
          const focused = focus?.section === "references" && focus.sceneId === scene.id;
          return (
            <article className={`reference-scene-card ${focused ? "focused" : ""}`} data-source-scene-id={scene.id} key={scene.id}>
              <header>
                <div>
                  <span>Scene {String(scene.number).padStart(2, "0")}</span>
                  <h3>{scene.slugline}</h3>
                  <p>{scene.synopsis}</p>
                </div>
                <em className={`readiness ${scene.readiness}`}>{sceneStatus(scene)}</em>
              </header>

              <div className="reference-action-row">
                <UploadAssetButton accept="image/*,.pdf" label="Upload reference" onFiles={(files) => onUploadSceneReference(scene.id, files)} />
                <button className="secondary-action" onClick={() => onSeedReference(scene.id)} type="button">EvoLink Seedance reference</button>
              </div>

              <div className="reference-asset-list">
                {sceneReferences.length ? sceneReferences.map((asset) => (
                  <div className={`reference-asset-row ${asset.disabled ? "disabled" : ""}`} key={asset.assetId || asset.label}>
                    <div className="reference-asset-main">
                      <div className="asset-status-row">
                        <span className={`source-pill ${asset.sourceMode || "user_upload"}`}>{referenceStatusLabel(asset)}</span>
                        {assetCloudStatusLabel(asset) ? <span className={`source-pill ${assetCloudStatusClass(asset)}`}>{assetCloudStatusLabel(asset)}</span> : null}
                        <span className={`source-pill parse-${asset.parseStatus || "unparsed"}`}>{referenceParseLabel(asset)}</span>
                      </div>
                      <strong>{asset.label}</strong>
                      <small>{asset.fileName}</small>
                      {asset.parsedReference ? <ParsedReferenceBrief asset={asset} /> : null}
                      {asset.parseError ? <small className="source-error">{asset.parseError}</small> : null}
                    </div>
                    <div className="source-action-row compact">
                      <button
                        className="secondary-action"
                        disabled={!asset.assetId || parsingReferenceId === asset.assetId}
                        onClick={() => onParseReference(asset)}
                        type="button"
                      >
                        {parsingReferenceId === asset.assetId ? "Parsing..." : asset.parsedReference ? "Re-analyze" : "Analyze board"}
                      </button>
                      {asset.parsedReference && asset.assetId ? (
                        <button className="secondary-action" onClick={() => onReviewReference(asset.assetId!)} type="button">
                          Review parse
                        </button>
                      ) : null}
                      <button className="secondary-action" onClick={() => asset.assetId && onToggleReferenceDisabled(asset.assetId)} type="button">
                        {asset.disabled ? "Enable" : "Disable"}
                      </button>
                      {assetRetryable(asset) ? (
                        <button className="secondary-action" onClick={() => onRetryAssetUpload("sceneReferences", asset.assetId)} type="button">
                          Retry upload
                        </button>
                      ) : null}
                    </div>
                  </div>
                )) : (
                  <p>No scene reference yet. Upload a lookbook frame or mark this scene for EvoLink generation.</p>
                )}
              </div>
            </article>
          );
        }) : (
          <section className="source-empty-state">
            <Icon name="image" />
            <strong>No scenes yet</strong>
            <p>Upload and analyze a script to create scenes, or add a lookbook/reference board now for later assignment.</p>
            <div className="source-empty-actions">
              <UploadAssetButton
                accept="image/*,.pdf"
                label="Upload reference"
                onFiles={(files) => onUploadAssets("sceneReferences", files, { kind: "scene_reference", label: "Unassigned scene reference" })}
              />
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function ReferenceParseReviewModal({ asset, onApply, onClose }: { asset: UploadedAssetRecord; onApply: () => void; onClose: () => void }) {
  const parsed = asset.parsedReference;
  if (!parsed) return null;
  const rows = referenceReviewRows(parsed);
  const applyDisabled = !asset.sceneId || asset.disabled;
  return (
    <div className="modal-backdrop" role="presentation">
      <section className="reference-review-modal" aria-label={`${asset.label} parsed reference review`} role="dialog" aria-modal="true">
        <header>
          <div>
            <span>Reference Board Parse</span>
            <h2>{parsed.title || asset.label}</h2>
            <p>Review what Kinetix extracted before using it in storyboard prompts or EvoLink dry-run packages.</p>
          </div>
          <button className="icon-button" onClick={onClose} type="button" aria-label="Close parsed reference review">
            x
          </button>
        </header>

        <div className="reference-review-note">
          <Icon name="lock" />
          <div>
            <strong>Upload remains the source of truth</strong>
            <p>Applying this parse adds prompt context and reference assignments only. It does not replace uploaded files, locks, boards, rights review, or provider-send gates.</p>
          </div>
        </div>

        {parsed.warnings.length ? (
          <div className="reference-review-warning">
            <strong>Parse warnings</strong>
            <p>{parsed.warnings.slice(0, 3).join(" ")}</p>
          </div>
        ) : null}

        <dl className="reference-review-grid">
          {rows.map((row) => (
            <div key={row.label}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>

        <div className="modal-actions">
          <button className="secondary-action" onClick={onClose} type="button">
            Keep as review only
          </button>
          <button className="primary-action" disabled={applyDisabled} onClick={onApply} type="button">
            Apply to scene boards
          </button>
        </div>
      </section>
    </div>
  );
}

function ParsedReferenceBrief({ asset }: { asset: UploadedAssetRecord }) {
  const parsed = asset.parsedReference;
  if (!parsed) return null;
  const rows = [
    { label: "World", value: parsed.storyWorld || parsed.location },
    { label: "Design", value: compactList(parsed.designNotes) },
    { label: "Camera", value: compactList(parsed.cameraLens) },
    { label: "Lighting", value: compactList(parsed.lightingAtmosphere) },
    { label: "Palette", value: compactList(parsed.colorPalette, 5) },
    { label: "Prompt", value: compactList(parsed.promptNotes, 4) }
  ].filter((row) => row.value);
  if (!rows.length) return null;
  return (
    <dl className="parsed-reference-brief" aria-label={`${asset.label} parsed reference details`}>
      {rows.slice(0, 5).map((row) => (
        <div key={row.label}>
          <dt>{row.label}</dt>
          <dd>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function UploadAssetButton({ accept, label, onFiles }: { accept: string; label: string; onFiles: (files: File[]) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        className="visually-hidden"
        type="file"
        multiple
        accept={accept}
        onChange={(event) => {
          const files = Array.from(event.target.files || []);
          if (files.length) onFiles(files);
          event.target.value = "";
        }}
      />
      <button className="secondary-action" onClick={() => inputRef.current?.click()} type="button">
        <Icon name="upload" />
        {label}
      </button>
    </>
  );
}
